import * as React from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LEGAL_ENTITY_KIND_LABELS } from "@/lib/legalEntityMessages";
import type { LegalEntityKind } from "@/lib/legalEntityApi";

export type UnverifiedFormValues = {
  kind: LegalEntityKind;
  shortName: string;
  legalName: string;
  email: string;
  phone: string;
  city: string;
  postalCode: string;
  street: string;
  buildingNumber: string;
};

type LegalEntityUnverifiedFormProps = {
  allowedKinds: LegalEntityKind[];
  busy: boolean;
  initialKind: LegalEntityKind;
  onSubmit: (values: UnverifiedFormValues) => void;
  onCancel: () => void;
};

function formatPostal(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 5);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
}

function optionalEmailOk(raw: string): boolean {
  const value = raw.trim();
  if (value.length === 0) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function optionalPhoneOk(raw: string): boolean {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 0) return true;
  return digits.length >= 9;
}

export function LegalEntityUnverifiedForm({
  allowedKinds,
  busy,
  initialKind,
  onSubmit,
  onCancel,
}: LegalEntityUnverifiedFormProps) {
  const { t } = useTranslation();
  const [kind, setKind] = React.useState<LegalEntityKind>(initialKind);
  const [shortName, setShortName] = React.useState("");
  const [legalName, setLegalName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [city, setCity] = React.useState("");
  const [postalCode, setPostalCode] = React.useState("");
  const [street, setStreet] = React.useState("");
  const [buildingNumber, setBuildingNumber] = React.useState("");

  const canSubmit =
    shortName.trim().length >= 3 &&
    legalName.trim().length >= 3 &&
    optionalEmailOk(email) &&
    optionalPhoneOk(phone) &&
    city.trim().length >= 2 &&
    /^\d{2}-\d{3}$/.test(postalCode);

  return (
    <div className="grid gap-3 rounded-md border border-amber-300/80 bg-amber-50/60 p-3 dark:border-amber-800 dark:bg-amber-950/30">
      <p className="text-sm text-amber-900 dark:text-amber-200">
        {t("admin.legalEntity.gusManualHint")}
      </p>
      <div className="grid gap-2">
        <Label>{t("admin.legalEntity.entityKind")}</Label>
        <Select value={kind} onValueChange={(v) => setKind(v as LegalEntityKind)} disabled={busy}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {allowedKinds.map((k) => (
              <SelectItem key={k} value={k}>
                {LEGAL_ENTITY_KIND_LABELS[k]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-2">
        <Label htmlFor="unverified-short">{t("admin.legalEntity.shortName")}</Label>
        <Input
          id="unverified-short"
          value={shortName}
          onChange={(e) => setShortName(e.target.value.slice(0, 80))}
          disabled={busy}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="unverified-legal">{t("admin.legalEntity.legalName")}</Label>
        <Input
          id="unverified-legal"
          value={legalName}
          onChange={(e) => setLegalName(e.target.value.slice(0, 200))}
          disabled={busy}
        />
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor="unverified-email">{t("admin.legalEntity.emailOptional")}</Label>
          <Input
            id="unverified-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="unverified-phone">{t("admin.legalEntity.phoneOptional")}</Label>
          <Input
            id="unverified-phone"
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor="unverified-city">{t("admin.legalEntity.city")}</Label>
          <Input
            id="unverified-city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="unverified-postal">{t("admin.legalEntity.postalCode")}</Label>
          <Input
            id="unverified-postal"
            inputMode="numeric"
            placeholder="00-000"
            value={postalCode}
            onChange={(e) => setPostalCode(formatPostal(e.target.value))}
            disabled={busy}
          />
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor="unverified-street">{t("admin.legalEntity.streetOptional")}</Label>
          <Input
            id="unverified-street"
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="unverified-building">{t("admin.legalEntity.buildingOptional")}</Label>
          <Input
            id="unverified-building"
            value={buildingNumber}
            onChange={(e) => setBuildingNumber(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          onClick={() =>
            onSubmit({
              kind,
              shortName: shortName.trim(),
              legalName: legalName.trim(),
              email: email.trim(),
              phone: phone.trim(),
              city: city.trim(),
              postalCode,
              street: street.trim(),
              buildingNumber: buildingNumber.trim(),
            })
          }
          disabled={busy || !canSubmit}
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {t("admin.legalEntity.addUnverified")}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
          {t("common.cancel")}
        </Button>
      </div>
    </div>
  );
}
